import React from "react";
import { CanonicalBusinessEvent } from "../../../../types/cbes";
import { filterNonNoOpCBEs } from "../../../../helpers/cbeUtils";
import { determineFlowTypeFromObjects } from "../../../../helpers/flowTypeUtils";
import { isCanonicalBusinessEvent } from "../../../../helpers/typeChecks";
import {
  AccountingEventsGroup,
  DigitalBusinessDocument,
  DigitalCostDocument,
  DigitalPaymentDocument,
} from "../../../../types";
import { Stage } from "../../../../lusca-search-api/generated-src";

type JsonDataType =
  | CanonicalBusinessEvent
  | AccountingEventsGroup
  | DigitalBusinessDocument
  | DigitalCostDocument
  | DigitalPaymentDocument;

interface CrossStageData {
  cbes: JsonDataType[];
  events: JsonDataType[];
  rawCbes?: CanonicalBusinessEvent[];
}

interface CrossStageRawData {
  totalCbes: number;
  totalEvents: number;
}

interface UseUsecaseDifferencesProps {
  crossStageEnabled: boolean;
  compareStage: Stage;
  currentStage: Stage;
  isCbe: boolean;
  isAccountingGroup: boolean;
  objects: JsonDataType[];
  allCurrentCbes?: CanonicalBusinessEvent[];
  crossStageData: CrossStageData;
  crossStageRawData: CrossStageRawData;
  crossStageLoading?: boolean;
}

interface UsecaseDifferencesResult {
  currentCount: number;
  compareCount: number;
  currentUsecases: string[];
  compareUsecases: string[];
  currentOnly: string[];
  compareOnly: string[];
  flowType: string;
  compareHasOtherFlowData: boolean;
  otherFlowUsecases: string[];
  currentOnlyOtherFlowUsecases: string[];
}

function getUsecaseNames(objects: JsonDataType[]): Set<string> {
  return new Set(
    objects
      .filter((obj): obj is CanonicalBusinessEvent =>
        isCanonicalBusinessEvent(obj)
      )
      .map((cbe) => cbe.usecaseName)
  );
}

function difference(a: Set<string>, b: Set<string>): string[] {
  return [...a].filter((item) => !b.has(item));
}

export const useUsecaseDifferences = ({
  crossStageEnabled,
  compareStage,
  currentStage,
  isCbe,
  isAccountingGroup,
  objects: currentFlowCbes,
  allCurrentCbes,
  crossStageData,
  crossStageRawData,
  crossStageLoading,
}: UseUsecaseDifferencesProps): UsecaseDifferencesResult | null => {
  return React.useMemo(() => {
    if (
      !crossStageEnabled ||
      compareStage === currentStage ||
      !isCbe ||
      isAccountingGroup ||
      crossStageLoading ||
      (crossStageRawData.totalCbes === 0 && crossStageRawData.totalEvents === 0)
    ) {
      return null;
    }

    // Scoped to the selected flow (e.g. Classic/Revenue or Document/Royalty)
    const currentStageUsecases = getUsecaseNames(currentFlowCbes);
    const compareStageUsecases = getUsecaseNames(crossStageData.cbes);

    // All flows combined (NoOps excluded)
    const currentStageAllFlows = allCurrentCbes
      ? new Set(filterNonNoOpCBEs(allCurrentCbes).map((cbe) => cbe.usecaseName))
      : currentStageUsecases;
    const compareStageAllFlows = crossStageData.rawCbes
      ? new Set(filterNonNoOpCBEs(crossStageData.rawCbes).map((cbe) => cbe.usecaseName))
      : compareStageUsecases;

    // --- Differences ---
    const currentOnly = difference(currentStageUsecases, compareStageUsecases);
    const compareOnly = difference(compareStageUsecases, currentStageUsecases);
    const hasSelectedFlowDiff = currentOnly.length > 0 || compareOnly.length > 0;

    // Usecases unique to one stage across ALL flows (e.g. Prod has StandaloneCostContentPartner but Gamma doesn't)
    const onlyOnCompare = difference(compareStageAllFlows, currentStageAllFlows);
    const onlyOnCurrent = difference(currentStageAllFlows, compareStageAllFlows);

    // Filter to those NOT already shown in the current flow section above
    const newInCompareOtherFlows = onlyOnCompare.filter(
      (uc) => !compareStageUsecases.has(uc)
    );
    const newInCurrentOtherFlows = onlyOnCurrent.filter(
      (uc) => !currentStageUsecases.has(uc)
    );

    const hasOtherFlowDiff =
      newInCompareOtherFlows.length > 0 || newInCurrentOtherFlows.length > 0;

    if (!hasSelectedFlowDiff && !hasOtherFlowDiff) {
      return null;
    }

    return {
      currentCount: currentStageUsecases.size,
      compareCount: compareStageUsecases.size,
      currentUsecases: [...currentStageUsecases],
      compareUsecases: [...compareStageUsecases],
      currentOnly: currentOnly,
      compareOnly: compareOnly,
      flowType: determineFlowTypeFromObjects(currentFlowCbes),
      compareHasOtherFlowData:
        crossStageRawData.totalCbes > crossStageData.cbes.length ||
        crossStageRawData.totalEvents > crossStageData.events.length,
      otherFlowUsecases: newInCompareOtherFlows,
      currentOnlyOtherFlowUsecases: newInCurrentOtherFlows,
    };
  }, [
    crossStageEnabled,
    compareStage,
    currentStage,
    isCbe,
    isAccountingGroup,
    currentFlowCbes,
    allCurrentCbes,
    crossStageData.cbes,
    crossStageData.events,
    crossStageData.rawCbes,
    crossStageRawData,
    crossStageLoading,
  ]);
};
